// src/pages/EventsNews.js
import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Breadcrumbs,
  Link,
} from "@mui/material";
import { keyframes } from "@mui/system";
import EastIcon from "@mui/icons-material/East";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import HeroWave from "../../../GlobalFunction/HeroWave";
import { getPrimaryColor } from "../../../GlobalFunction/Theme";

// ✅ Define ALL_EVENTS at the top
const ALL_EVENTS = [
  { id: 1,  title: "電源水快閃活動開跑了！", date: "2026年1月10日至1月24日", img: "/img/news/news1.jpg", link: "#" },
  { id: 2,  title: "2026年食品展參展資訊",   date: "2026年1月10日至1月24日", img: "/img/news/news2.jpg", link: "#" },
  { id: 3,  title: "電源水快閃活動開跑了！", date: "2026年1月10日至1月24日", img: "/img/news/news3.jpg", link: "#" },
  { id: 4,  title: "新品發表會資訊",         date: "2026年3月5日至3月7日",   img: "/img/aboutus/history/圖片1.jpg", link: "#" },
  { id: 5,  title: "年度夥伴說明會",         date: "2026年4月12日至4月13日", img: "/img/companyInfo/recruit5.jpg", link: "#" },
];

// 🔄 Animations
const arrowOrbit = keyframes`
  0%   { transform: translateX(0); opacity: 1; }
  30%  { transform: translateX(100%); opacity: 0; }
  60%  { transform: translateX(-150%); opacity: 0; }
  100% { transform: translateX(0); opacity: 1; }
`;
const slideUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export default function EventsNews({mode}) {
  const BATCH = 5;
  const [visibleCount, setVisibleCount] = useState(BATCH);
  const prevCountRef = useRef(visibleCount);
  const itemRefs = useRef([]);

  // Scroll down to follow new items
  useEffect(() => {
    const prev = prevCountRef.current;
    if (visibleCount > prev) {
      const lastNewIndex = visibleCount - 1;
      const lastEl = itemRefs.current[lastNewIndex];
      if (lastEl) {
        lastEl.scrollIntoView({ behavior: "smooth", block: "end" });
      }
      prevCountRef.current = visibleCount;
    }
  }, [visibleCount]);

  const events = useMemo(
    () => ALL_EVENTS.slice(0, Math.min(visibleCount, ALL_EVENTS.length)),
    [visibleCount]
  );

  const handleSeeMore = () => {
    setVisibleCount((c) => Math.min(c + BATCH, ALL_EVENTS.length));
  };

  const showSeeMore = visibleCount < ALL_EVENTS.length;

  return (
    <Box>
      <HeroWave image={"/img/news/eventsTop.png"} h3={"活動資訊"} />

      {/* ✅ Breadcrumbs */}
      <Container maxWidth="lg" sx={{ mt: 2 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          <Link underline="hover" color="inherit" href={"/"}>首頁</Link>
          <Link underline="hover" color="inherit" href={"/最新資訊"}>{"最新資訊"}</Link>
          <Typography color="text.primary">活動資訊</Typography>
        </Breadcrumbs>
      </Container>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ maxWidth: 860, mx: "auto" }}>
          <Grid container direction="column" spacing={3}>
            {events.map((event, idx) => {
              const prev = prevCountRef.current;
              const isNew = idx >= prev && idx < visibleCount;
              const stagger = Math.max(0, idx - prev) * 70;

              return (
                <Grid
                  item
                  key={event.id}
                  ref={(el) => (itemRefs.current[idx] = el)}
                  sx={ isNew ? {
                    animation: `${slideUp} 420ms ease-out both`,
                    animationDelay: `${stagger}ms`,
                  } : undefined}
                >
                  <Card elevation={0} sx={{ display: "flex", alignItems: "center", borderRadius: 2, bgcolor: "transparent" }}>
                    <CardMedia
                      component="img"
                      image={event.img}
                      alt={event.title}
                      sx={{
                        width: { xs: 132, md: 296 },
                        height: { xs: 99, md: 187 },
                        borderRadius: 1.5,
                        objectFit: "cover",
                        bgcolor: "#eee",
                      }}
                    />
                    <CardContent sx={{ flex: 1, pl: { xs: 2, md: 3 }, display: "flex", flexDirection: "column" }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{event.title}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{event.date}</Typography>
                      <Box sx={{ mt: 2 }}>
                        <Button
                          variant="contained"
                          endIcon={<EastIcon />}
                          href={event.link}
                          sx={{
                            bgcolor: getPrimaryColor(mode),
                            color: "#fff",
                            borderRadius: 10,
                            textTransform: "none",
                            px: 3,
                            py: 1,
                            fontWeight: 700,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: 4,
                              "& .MuiSvgIcon-root": { animation: `${arrowOrbit} 0.8s ease forwards` },
                            },
                            "& .MuiSvgIcon-root": { transition: "transform 0.3s ease" },
                          }}
                        >
                          了解更多
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {showSeeMore && (
            <Box sx={{ textAlign: "center", mt: 4 }}>
              <Button
                onClick={handleSeeMore}
                variant="outlined"
                sx={{
                  borderRadius: 999,
                  px: 3.5,
                  py: 1.1,
                  fontWeight: 700,
                  textTransform: "none",
                  borderColor: getPrimaryColor(mode),
                  color: getPrimaryColor(mode),
                  transformOrigin: "center",
                  transition:
                    "transform 220ms cubic-bezier(.34,1.56,.64,1), box-shadow 220ms ease, background-color 220ms ease",
                  "&:hover": {
                    bgcolor: "rgba(0,0,0,0.02)",
                    borderColor: getPrimaryColor(mode),
                    transform: "scale(1.06)",
                    boxShadow: 3,
                  },
                  "&:active": { transform: "scale(0.98)" },
                }}
              >
                看更多
              </Button>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
}
